import type { BamAnswerOption, BamDefinition, BamSingleSelectQuestion } from './schema';

// The one `active` BAM questionnaire seeded by migration. Authored here as the
// single source so the seed migration SQL, the seed-parses unit test, and the
// real-DB integration test all agree. The set is a 14-item check-in with item 4
// expanded into the q4a–q4g drug-class grid (20 answerable single_select
// items). Each option `value` is the official BAM item code (0–4): the coded
// score a chosen option contributes. Direction is baked into the value —
// day/severity/health items code low→high, the income item codes No=0/Yes=4,
// and the satisfaction item is reverse-coded (see `severityScaleReverse`).
// Subscale grouping is layered on top of these per-item codes later.

export const bamSeedQuestionnaireId = '5ba00000-0000-7000-8000-000000000001';
export const bamSeedSlug = 'bam';
export const bamSeedVersion = 1;
export const bamSeedTitle = 'Brief Addiction Monitor';

const healthScale = (prefix: string): BamAnswerOption[] => [
  { key: `${prefix}_excellent`, label: 'Excellent', value: 0 },
  { key: `${prefix}_very_good`, label: 'Very good', value: 1 },
  { key: `${prefix}_good`, label: 'Good', value: 2 },
  { key: `${prefix}_fair`, label: 'Fair', value: 3 },
  { key: `${prefix}_poor`, label: 'Poor', value: 4 },
];

const dayBuckets = (prefix: string): BamAnswerOption[] => [
  { key: `${prefix}_0`, label: '0 days', value: 0 },
  { key: `${prefix}_1_3`, label: '1–3 days', value: 1 },
  { key: `${prefix}_4_8`, label: '4–8 days', value: 2 },
  { key: `${prefix}_9_15`, label: '9–15 days', value: 3 },
  { key: `${prefix}_16_30`, label: '16–30 days', value: 4 },
];

const severityScale = (prefix: string): BamAnswerOption[] => [
  { key: `${prefix}_not_at_all`, label: 'Not at all', value: 0 },
  { key: `${prefix}_slightly`, label: 'Slightly', value: 1 },
  { key: `${prefix}_moderately`, label: 'Moderately', value: 2 },
  { key: `${prefix}_considerably`, label: 'Considerably', value: 3 },
  { key: `${prefix}_extremely`, label: 'Extremely', value: 4 },
];

// The satisfaction item is reverse-coded in the official BAM: greater
// satisfaction is the low (protective) code. Labels read the same; only the
// coded `value` flips.
const severityScaleReverse = (prefix: string): BamAnswerOption[] => [
  { key: `${prefix}_not_at_all`, label: 'Not at all', value: 4 },
  { key: `${prefix}_slightly`, label: 'Slightly', value: 3 },
  { key: `${prefix}_moderately`, label: 'Moderately', value: 2 },
  { key: `${prefix}_considerably`, label: 'Considerably', value: 1 },
  { key: `${prefix}_extremely`, label: 'Extremely', value: 0 },
];

const yesNo = (prefix: string): BamAnswerOption[] => [
  { key: `${prefix}_yes`, label: 'Yes', value: 4 },
  { key: `${prefix}_no`, label: 'No', value: 0 },
];

const single = (
  name: string,
  prompt: string,
  options: BamAnswerOption[],
): BamSingleSelectQuestion => ({ type: 'single_select', name, prompt, options });

const drugGrid: BamSingleSelectQuestion[] = [
  ['q4a_alcohol', 'alcohol'],
  ['q4b_cannabis', 'cannabis or marijuana'],
  ['q4c_opioids', 'heroin or other opioids'],
  ['q4d_cocaine', 'cocaine or crack'],
  ['q4e_stimulants', 'methamphetamine or other stimulants'],
  ['q4f_sedatives', 'sedatives, benzodiazepines, or sleeping pills'],
  ['q4g_other', 'any other drugs'],
].map(([name, substance]) =>
  single(name, `In the past 30 days, how many days did you use ${substance}?`, dayBuckets(name)),
);

const questions: BamSingleSelectQuestion[] = [
  single(
    'q1_alcohol_any',
    'In the past 30 days, how many days did you drink any alcohol?',
    dayBuckets('q1'),
  ),
  single(
    'q2_heavy_drinking',
    'In the past 30 days, how many days did you drink at least 4 drinks (if female) or 5 drinks (if male)?',
    dayBuckets('q2'),
  ),
  single(
    'q3_drug_use_any',
    'In the past 30 days, how many days did you use any illegal or street drugs, or take any prescribed medicines more than they were prescribed?',
    dayBuckets('q3'),
  ),
  ...drugGrid,
  single(
    'q5_cravings',
    'In the past 30 days, how much were you bothered by cravings or urges to drink alcohol or use drugs?',
    severityScale('q5'),
  ),
  single(
    'q6_physical_health',
    'In the past 30 days, would you say your physical health has been:',
    healthScale('q6'),
  ),
  single(
    'q7_sleep_trouble',
    'In the past 30 days, how many days did you have trouble falling asleep or staying asleep?',
    dayBuckets('q7'),
  ),
  single(
    'q8_low_mood',
    'In the past 30 days, how many days have you felt depressed, anxious, angry, or very upset throughout most of the day?',
    dayBuckets('q8'),
  ),
  single(
    'q9_progress_satisfaction',
    'How satisfied are you with your progress toward achieving your recovery goals?',
    severityScaleReverse('q9'),
  ),
  single(
    'q10_confidence',
    'How confident are you in your ability to reduce or abstain from alcohol and drug use in the next 30 days?',
    severityScale('q10'),
  ),
  single(
    'q11_risk_exposure',
    'In the past 30 days, how many days were you in any situations or with any people that might put you at an increased risk for using alcohol or drugs?',
    dayBuckets('q11'),
  ),
  single(
    'q12_income_adequate',
    'Do you have enough income (from legal sources) to pay for necessities such as housing, food and clothing for yourself and your dependents?',
    yesNo('q12'),
  ),
  single(
    'q13_relationship_conflict',
    'In the past 30 days, how much have you been bothered by arguments or problems getting along with any family members or friends?',
    severityScale('q13'),
  ),
  single(
    'q14_supportive_contact',
    'In the past 30 days, how many days were you in contact with family members or friends who are supportive of your goals relating to use of substances?',
    dayBuckets('q14'),
  ),
];

export const bamSeedDefinition: BamDefinition = { questions };

// Version 2: the same items, option keys and option codes as version 1, plus
// member context. Question 2's threshold wording is chosen from the member's
// heavy-drinking threshold (the official wording keeps both thresholds when the
// threshold is unknown), and questions 1 and 2 carry a confirm-only hint from
// the member's recent alcohol check-ins. The provider names are data here; the
// server checks they are registered providers.
export const bamSeedV2QuestionnaireId = '5ba00000-0000-7000-8000-000000000002';
export const bamSeedV2Version = 2;
export const BAM_SEED_THRESHOLD_PROVIDER = 'heavy_drinking_threshold';
export const BAM_SEED_ALCOHOL_CHECKINS_PROVIDER = 'alcohol_checkins_30d';
export const bamSeedAlcoholAnyQuestion = 'q1_alcohol_any';
export const bamSeedHeavyDrinkingQuestion = 'q2_heavy_drinking';

const withContext = (question: BamSingleSelectQuestion): BamSingleSelectQuestion => {
  if (question.name === bamSeedAlcoholAnyQuestion) {
    return { ...question, hintContextPath: `${BAM_SEED_ALCOHOL_CHECKINS_PROVIDER}/q1Hint` };
  }
  if (question.name === bamSeedHeavyDrinkingQuestion) {
    return {
      ...question,
      promptVariants: {
        contextKey: BAM_SEED_THRESHOLD_PROVIDER,
        byValue: {
          '4': 'In the past 30 days, how many days did you drink at least 4 drinks?',
          '5': 'In the past 30 days, how many days did you drink at least 5 drinks?',
        },
      },
      hintContextPath: `${BAM_SEED_ALCOHOL_CHECKINS_PROVIDER}/q2Hint`,
    };
  }
  return question;
};

export const bamSeedDefinitionV2: BamDefinition = {
  contextProviders: [BAM_SEED_THRESHOLD_PROVIDER, BAM_SEED_ALCOHOL_CHECKINS_PROVIDER],
  questions: questions.map(withContext),
};

// Version 3: the same items, option keys, option codes, wording variants, hints
// and providers as version 2, plus the official form's skip rules. The drug
// grid (q4a–q4g) is not asked when the drug-use item is "0 days", and the
// heavy-drinking item is not asked when the alcohol item is "0 days"; each
// skipped item stands for its own "0 days" option when read. The rules are
// definition data: the client turns them into navigation and never learns what
// a question means.
export const bamSeedV3QuestionnaireId = '5ba00000-0000-7000-8000-000000000003';
export const bamSeedV3Version = 3;
export const bamSeedDrugUseQuestion = 'q3_drug_use_any';
export const bamSeedDrugGridQuestions = [
  'q4a_alcohol',
  'q4b_cannabis',
  'q4c_opioids',
  'q4d_cocaine',
  'q4e_stimulants',
  'q4f_sedatives',
  'q4g_other',
] as const;

const drugGridNames: ReadonlySet<string> = new Set(bamSeedDrugGridQuestions);

const zeroDaysOption = (question: BamSingleSelectQuestion): string => {
  const zero = question.options.find((o) => o.value === 0);
  if (!zero) throw new Error(`${question.name} has no 0-coded option to imply`);
  return zero.key;
};

const withSkipRules = (question: BamSingleSelectQuestion): BamSingleSelectQuestion => {
  if (question.name === bamSeedHeavyDrinkingQuestion) {
    return {
      ...question,
      skippedWhen: {
        question: bamSeedAlcoholAnyQuestion,
        equals: 'q1_0',
        impliedOptionKey: zeroDaysOption(question),
      },
    };
  }
  if (drugGridNames.has(question.name)) {
    return {
      ...question,
      skippedWhen: {
        question: bamSeedDrugUseQuestion,
        equals: 'q3_0',
        impliedOptionKey: zeroDaysOption(question),
      },
    };
  }
  return question;
};

export const bamSeedDefinitionV3: BamDefinition = {
  contextProviders: bamSeedDefinitionV2.contextProviders,
  questions: questions.map(withContext).map(withSkipRules),
};
